import { useQueryClient } from "@tanstack/react-query";
import { RefCallback, useCallback, useMemo } from "react";
import { startTransition } from "react";

import { rqClient } from "@/shared/api/instance";
import type { ApiSchemas } from "@/shared/api/schema";
import { useSession } from "@/shared/model/session";

// ============================================================================
// Types
// ============================================================================

export type Notification = ApiSchemas["Notification"];
type NotificationsResponse = ApiSchemas["NotificationPreviewListResponse"];

export interface NotificationWithActions extends Notification {
  onClick: () => void;
  onMarkAsRead: () => void;
}

// ============================================================================
// Hook
// ============================================================================

export function useNotificationsModel(limit: number = 20) {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useSession();

  // ============================================================================
  // Notifications Query (infinite scroll)
  // ============================================================================

  const {
    fetchNextPage,
    data,
    isFetchingNextPage,
    isPending,
    hasNextPage,
    refetch,
  } = rqClient.useInfiniteQuery(
    "get",
    "/api/v1/notifications",
    {
      params: {
        query: { page: 1, limit },
      },
    },
    {
      initialPageParam: 1,
      pageParamName: "page",
      getNextPageParam: (
        lastPage: NotificationsResponse,
        _lastPageData: unknown,
        lastPageParams: unknown,
      ) => {
        if (!lastPage.pagination?.pages) return null;
        return Number(lastPageParams) < lastPage.pagination.pages
          ? Number(lastPageParams) + 1
          : null;
      },
      enabled: isAuthenticated,
    },
  );

  // Flatten notifications from all pages
  const notifications: Notification[] = useMemo(
    () => data?.pages.flatMap((page) => page.data ?? []) ?? [],
    [data],
  );

  // Intersection observer cursor for infinite scroll
  const cursorRef: RefCallback<HTMLDivElement> = useCallback(
    (el) => {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        { threshold: 0.5 },
      );

      if (el) {
        observer.observe(el);
        return () => observer.disconnect();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  // ============================================================================
  // Detail Query
  // ============================================================================

  const getNotificationDetail = useCallback(
    async (id: string) => {
      const result = await queryClient.fetchQuery(
        rqClient.queryOptions("get", "/api/v1/notifications/{id}", {
          params: { path: { id } },
        }),
      );
      return result;
    },
    [queryClient],
  );

  // ============================================================================
  // Mutations
  // ============================================================================

  // Mark as read mutation
  const markReadMutation = rqClient.useMutation(
    "patch",
    "/api/v1/notifications/{id}",
    {
      onSettled: async () => {
        await queryClient.invalidateQueries(
          rqClient.queryOptions("get", "/api/v1/notifications"),
        );
        await queryClient.invalidateQueries(
          rqClient.queryOptions("get", "/api/v1/notifications/unread_count"),
        );
      },
    },
  );

  // Mark all as read mutation
  const markAllReadMutation = rqClient.useMutation(
    "patch",
    "/api/v1/notifications/mark_all_read",
    {
      onSettled: async () => {
        await queryClient.invalidateQueries(
          rqClient.queryOptions("get", "/api/v1/notifications"),
        );
        await queryClient.invalidateQueries(
          rqClient.queryOptions("get", "/api/v1/notifications/unread_count"),
        );
      },
    },
  );

  // ============================================================================
  // Actions
  // ============================================================================

  const markAsRead = useCallback(
    (id: string) => {
      startTransition(async () => {
        await markReadMutation.mutateAsync({
          params: { path: { id } },
          body: {},
        });
      });
    },
    [markReadMutation],
  );

  const markAllAsRead = useCallback(() => {
    startTransition(async () => {
      await markAllReadMutation.mutateAsync({
        body: {},
      });
    });
  }, [markAllReadMutation]);

  // ============================================================================
  // Utilities
  // ============================================================================

  const unreadCount = useMemo(
    () => notifications.filter((n) => n.unread).length,
    [notifications],
  );

  const isEmpty = notifications.length === 0 && !isPending;

  // ============================================================================
  // Return
  // ============================================================================

  return {
    // Items
    notifications,
    isEmpty,
    unreadCount,

    // Infinite scroll
    cursorRef,
    isFetchingNextPage,
    hasNextPage,

    // Detail
    getNotificationDetail,

    // Actions
    markAsRead,
    markAllAsRead,
    refetch,

    // State
    isPending: isPending,
    isError: false,
    isMarkingRead: markReadMutation.isPending,
    isMarkingAllRead: markAllReadMutation.isPending,
  };
}

export type NotificationsModel = ReturnType<typeof useNotificationsModel>;
